import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Input,
  Row,
  Col,
  Table,
  Badge,
  Nav,
  NavItem,
  NavLink
} from 'reactstrap';
import moment from 'moment';
import { toast, Zoom } from 'react-toastify';
import { Copy, Share2, Check, Link2, PlusCircle, List, Trash2 } from 'react-feather';
import {
  Create_Booking_Invite,
  Get_Booking_Invites,
  Delete_Booking_Invite
} from '../../api/AccommodationApi/AccommodationApi';

const GenerateBookingLinkModal = ({ isOpen, toggle }) => {
  const [activeTab, setActiveTab] = useState('generate');
  const [checkInDateTime, setCheckInDateTime] = useState('');
  const [checkOutDateTime, setCheckOutDateTime] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestMobile, setGuestMobile] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // List of invites
  const [invites, setInvites] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchInvites = async () => {
    setListLoading(true);
    try {
      const { data } = await Get_Booking_Invites({ pageNumber: 1, recordsPerPage: 20 });
      if (data?.status === 200) {
        setInvites(data.payload.data || []);
      }
    } catch (error) {
      console.error('Error fetching invites:', error);
    } finally {
      setListLoading(false);
    }
  };

  const handleDelete = async (inviteId) => {
    if (!window.confirm('Are you sure you want to delete this booking link? The guest will no longer be able to use it.')) {
      return;
    }
    setDeletingId(inviteId);
    try {
      const { data } = await Delete_Booking_Invite(inviteId);
      if (data?.status === 200) {
        toast.success('Booking link deleted successfully!', { autoClose: 1500, transition: Zoom });
        fetchInvites();
        if (generatedLink?.inviteId === inviteId) {
          setGeneratedLink(null);
        }
      } else {
        toast.error(data?.message || 'Failed to delete booking link', { autoClose: 2000, transition: Zoom });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting booking link', { autoClose: 2000, transition: Zoom });
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInvites();
    } else {
      setGeneratedLink(null);
      setCopied(false);
      setCheckInDateTime('');
      setCheckOutDateTime('');
      setGuestName('');
      setGuestMobile('');
      setMaxGuests('');
    }
  }, [isOpen]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!checkInDateTime || !checkOutDateTime) {
      toast.error('Please select both Check-In and Check-Out dates/times', { autoClose: 2000, transition: Zoom });
      return;
    }
    const now = moment();
    if (moment(checkInDateTime).isBefore(now.clone().subtract(2, 'minutes'))) {
      toast.error('Check-In date cannot be in the past', { autoClose: 2000, transition: Zoom });
      return;
    }
    if (moment(checkOutDateTime).isSameOrBefore(moment(checkInDateTime))) {
      toast.error('Check-Out must be after Check-In', { autoClose: 2000, transition: Zoom });
      return;
    }

    setLoading(true);
    try {
      const cleanMobile = guestMobile.trim().replace(/[\s-]/g, '');
      const payload = {
        checkInDateTime: moment(checkInDateTime).toISOString(),
        checkOutDateTime: moment(checkOutDateTime).toISOString(),
        guestName: guestName.trim() || undefined,
        guestMobile: cleanMobile || undefined,
        maxGuests: maxGuests ? Number(maxGuests) : undefined
      };

      const { data } = await Create_Booking_Invite(payload);
      if (data?.status === 201 || data?.status === 200) {
        toast.success('Booking link generated successfully!', { autoClose: 1500, transition: Zoom });
        setGeneratedLink(data.payload);
        fetchInvites();
      } else {
        toast.error(data?.message || 'Failed to generate link', { autoClose: 2000, transition: Zoom });
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.payload?.error ||
        error?.message ||
        'Error generating link';
      toast.error(errMsg, { autoClose: 3000, transition: Zoom });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied to clipboard!', { autoClose: 1200, transition: Zoom });
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnWhatsApp = (item) => {
    const url = item.inviteUrl || `${window.location.origin}/accommodation/invite/${item.inviteToken}`;
    const checkIn = moment(item.checkInDateTime).format('DD/MM/YYYY hh:mm A');
    const checkOut = moment(item.checkOutDateTime).format('DD/MM/YYYY hh:mm A');
    const greeting = item.guestName ? `Namaste ${item.guestName},` : 'Namaste,';
    const guestLimitText = item.maxGuests ? `\n👥 Max Allowed Guests: Up to ${item.maxGuests} person(s)` : '';

    const text = `${greeting}\n\nHere is your private accommodation booking link:\n📅 Check-In: ${checkIn}\n📅 Check-Out: ${checkOut}${guestLimitText}\n\n🔗 Click to complete booking & payment:\n${url}\n\n*Note: This link is valid for 48 hours and can only be used once.*`;

    const waUrl = `https://wa.me/${item.guestMobile ? item.guestMobile.replace(/\D/g, '') : ''}?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
  };

  const getStatusBadge = (status, expiresAt) => {
    if (status === 'used') {
      return <Badge color="secondary">Used</Badge>;
    }
    if (status === 'expired' || new Date() > new Date(expiresAt)) {
      return <Badge color="danger">Expired</Badge>;
    }
    return <Badge color="success">Active</Badge>;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <Link2 className="me-2 text-primary" size={22} />
          <span>Guest Booking Link Generator</span>
        </div>
      </ModalHeader>
      <ModalBody>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === 'generate' ? 'active' : ''}
              onClick={() => setActiveTab('generate')}
              style={{ cursor: 'pointer' }}
            >
              <PlusCircle size={14} className="me-1" /> Generate New Link
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'list' ? 'active' : ''}
              onClick={() => {
                setActiveTab('list');
                fetchInvites();
              }}
              style={{ cursor: 'pointer' }}
            >
              <List size={14} className="me-1" /> View Generated Links ({invites.length})
            </NavLink>
          </NavItem>
        </Nav>

        {activeTab === 'generate' && (
          <div>
            <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
              <strong>ℹ️ Note:</strong> The dates selected here will be <strong>locked</strong> on the guest booking
              page. The link is <strong>one-time use only</strong> and valid for <strong>48 hours</strong>. Once paid, it
              cannot be used again.
            </div>

            <Form onSubmit={handleGenerate}>
              <Row>
                <Col md="6" className="mb-3">
                  <Label className="form-label fw-bold">
                    Check-In Date & Time <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    className="form-control"
                    value={checkInDateTime}
                    min={moment().format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => {
                      const newCheckIn = e.target.value;
                      setCheckInDateTime(newCheckIn);
                      if (checkOutDateTime && moment(checkOutDateTime).isSameOrBefore(moment(newCheckIn))) {
                        setCheckOutDateTime('');
                      }
                    }}
                    required
                  />
                </Col>

                <Col md="6" className="mb-3">
                  <Label className="form-label fw-bold">
                    Check-Out Date & Time <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    className="form-control"
                    value={checkOutDateTime}
                    min={checkInDateTime || moment().format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => setCheckOutDateTime(e.target.value)}
                    required
                  />
                </Col>

                <Col md="6" className="mb-3">
                  <Label className="form-label">Guest Name (Optional)</Label>
                  <Input
                    type="text"
                    placeholder="Enter guest name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                  />
                </Col>

                <Col md="6" className="mb-3">
                  <Label className="form-label">Guest Mobile Number (Optional)</Label>
                  <Input
                    type="tel"
                    placeholder="10 digit mobile number"
                    maxLength={10}
                    value={guestMobile}
                    onChange={(e) => setGuestMobile(e.target.value)}
                  />
                </Col>

                <Col md="6" className="mb-3">
                  <Label className="form-label">Max Allowed Guests / Persons (Optional)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    placeholder="e.g. 2 (leave blank for unlimited)"
                    value={maxGuests}
                    onChange={(e) => setMaxGuests(e.target.value)}
                  />
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    Restricts the maximum number of guests/persons the user can add in the form.
                  </small>
                </Col>
              </Row>

              <div className="text-end mt-2">
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? 'Generating...' : 'Generate Booking Link'}
                </Button>
              </div>
            </Form>

            {generatedLink && (
              <div
                className="mt-4 p-3 rounded"
                style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}
              >
                <h6 className="text-success fw-bold mb-2">🎉 Link Generated Successfully!</h6>
                <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                  Share this link with <strong>{generatedLink.guestName || 'Guest'}</strong>. Valid for 48 hours until{' '}
                  {moment(generatedLink.expiresAt).format('DD MMM YYYY, hh:mm A')}.
                  {generatedLink.maxGuests && (
                    <span className="badge bg-primary ms-2">Max {generatedLink.maxGuests} Guest(s)</span>
                  )}
                </p>

                <div className="input-group mb-2">
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    value={
                      generatedLink.inviteUrl ||
                      `${window.location.origin}/accommodation/invite/${generatedLink.inviteToken}`
                    }
                  />
                  <Button
                    color={copied ? 'success' : 'outline-primary'}
                    onClick={() =>
                      copyToClipboard(
                        generatedLink.inviteUrl ||
                          `${window.location.origin}/accommodation/invite/${generatedLink.inviteToken}`
                      )
                    }
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => shareOnWhatsApp(generatedLink)}
                  >
                    <Share2 size={14} className="me-1" /> Share on WhatsApp
                  </Button>
                  <Button
                    color="outline-danger"
                    size="sm"
                    disabled={deletingId === generatedLink.inviteId}
                    onClick={() => handleDelete(generatedLink.inviteId)}
                  >
                    <Trash2 size={14} className="me-1" /> Delete Link
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'list' && (
          <div>
            {listLoading ? (
              <p className="text-center py-4">Loading invites...</p>
            ) : invites.length === 0 ? (
              <p className="text-center text-muted py-4">No booking links generated yet.</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table responsive size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '120px' }}>Guest</th>
                      <th style={{ minWidth: '140px' }}>Stay Dates</th>
                      <th style={{ minWidth: '90px' }}>Max Guests</th>
                      <th style={{ minWidth: '80px' }}>Status</th>
                      <th style={{ minWidth: '110px' }}>Expires At</th>
                      <th style={{ minWidth: '115px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((item) => {
                      const fullUrl = `${window.location.origin}/accommodation/invite/${item.inviteToken}`;
                      return (
                        <tr key={item.inviteId}>
                          <td>
                            <strong>{item.guestName || '--'}</strong>
                            {item.guestMobile && (
                              <div className="text-muted" style={{ fontSize: '11px' }}>
                                {item.guestMobile}
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            <div>In: {moment(item.checkInDateTime).format('DD/MM/YY HH:mm')}</div>
                            <div>Out: {moment(item.checkOutDateTime).format('DD/MM/YY HH:mm')}</div>
                          </td>
                          <td>
                            {item.maxGuests ? (
                              <Badge color="info">Max {item.maxGuests}</Badge>
                            ) : (
                              <span className="text-muted" style={{ fontSize: '12px' }}>Unlimited</span>
                            )}
                          </td>
                          <td>{getStatusBadge(item.status, item.expiresAt)}</td>
                          <td style={{ fontSize: '12px' }}>
                            {moment(item.expiresAt).format('DD/MM/YY HH:mm')}
                          </td>
                          <td>
                            <div className="d-flex justify-content-center gap-1">
                              <Button
                                size="sm"
                                color="outline-primary"
                                className="px-2"
                                title="Copy Link"
                                onClick={() => copyToClipboard(fullUrl)}
                              >
                                <Copy size={12} />
                              </Button>
                              <Button
                                size="sm"
                                color="outline-success"
                                className="px-2"
                                title="Share on WhatsApp"
                                onClick={() => shareOnWhatsApp(item)}
                              >
                                <Share2 size={12} />
                              </Button>
                              <Button
                                size="sm"
                                color="outline-danger"
                                className="px-2"
                                title="Delete Link"
                                disabled={deletingId === item.inviteId}
                                onClick={() => handleDelete(item.inviteId)}
                              >
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GenerateBookingLinkModal;
